import { useCallback, useEffect, useRef, useState } from 'react';
import { apiJson } from '@/lib/api';
import {
  type DocumentsConfig,
  type GlobalDocumentsStore,
  type ProjectDocument,
  defaultGlobalDocumentsConfig,
  normalizeGlobalDocumentsStore,
} from '@/store/projectStore';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Загрузка/сохранение глобального архива документов ДИАКа (`/api/workspace/global-documents`).
 * Сохранение — с небольшим debounce, чтобы inline-правки структуры не заваливали сервер.
 *
 * `loading` — true до первой загрузки; важно не рендерить пустые списки как
 * «ничего нет», пока мы ещё не получили данные.
 */
export function useGlobalDocuments() {
  const { user, canEdit } = useAuth();
  const [store, setStore] = useState<GlobalDocumentsStore>(() => ({
    config: defaultGlobalDocumentsConfig(),
    documents: [],
  }));
  const [loading, setLoading] = useState(true);
  const skipSave = useRef(true);

  useEffect(() => {
    if (!user) {
      setStore({ config: defaultGlobalDocumentsConfig(), documents: [] });
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    skipSave.current = true;
    apiJson<unknown>('/api/workspace/global-documents')
      .then((raw) => {
        if (cancelled) return;
        setStore(normalizeGlobalDocumentsStore(raw));
      })
      .catch(() => {
        if (cancelled) return;
        setStore({ config: defaultGlobalDocumentsConfig(), documents: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !canEdit || loading) return;
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    const t = setTimeout(() => {
      apiJson('/api/workspace/global-documents', {
        method: 'PUT',
        body: JSON.stringify(store),
      }).catch(() => {});
    }, 650);
    return () => clearTimeout(t);
  }, [store, user, canEdit, loading]);

  const applyChange = useCallback(
    (next: { documents: ProjectDocument[]; config: DocumentsConfig }) => {
      setStore({ config: next.config, documents: next.documents });
    },
    [],
  );

  return { store, loading, applyChange, canEdit };
}
